import { createContext, useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from '../axios';

export const AuthContext = createContext();

// Helper to decode Base64URL (used in JWT)
function decodeBase64Url(str) {
  const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
  return atob(base64 + pad);
}

// Check if JWT is expired
function isTokenExpired(token) {
  try {
    const [, payload] = token.split('.');
    if (!payload) return true;
    const json = JSON.parse(decodeBase64Url(payload));
    if (!json.exp) return true;
    return Date.now() >= json.exp * 1000; // convert exp from seconds to ms
  } catch {
    return true;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('user')));
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const location = useLocation();

  // Login function: store user and access token
  const login = useCallback((userData, accessToken) => {
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', accessToken);
    setUser(userData);
    setToken(accessToken);
  }, []);

  // Logout function: clear user and token
  const logout = useCallback(() => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
    setToken(null);
  }, []);
/*
  // Refresh JWT token using backend
  const refreshAccessToken = useCallback(async () => {
    try {
      const { data } = await axios.post('/auth/refresh-token');
      if (data?.token) {
        localStorage.setItem('token', data.token);
        setToken(data.token);
        return data.token;
      }
      throw new Error('No token in refresh response');
    } catch {
      logout();
      return null;
    }
  }, [logout]);
*/
// Refresh JWT token using backend
const refreshAccessToken = useCallback(async () => {
  try {
    const { data } = await axios.post('/auth/refresh-token', null, {
      withCredentials: true
    });

    if (data?.token) {
      localStorage.setItem('token', data.token);
      setToken(data.token);
      return data.token;
    }

    throw new Error('No token in refresh response');
  } catch {
    logout();
    return null;
  }
}, [logout]);
  // Immediately logout if token is expired
  useEffect(() => {
    if (token && isTokenExpired(token)) {
      logout();
    }
  }, [token, logout]);

  // Automatically refresh token unless on login/register pages
  useEffect(() => {
    const onAuthPage = ['/login', '/register'].includes(location.pathname);
    if (token && !onAuthPage) {
      refreshAccessToken();
    }
  }, [token, location.pathname, refreshAccessToken]);

  const isAdmin = user?.role === 'admin';
  const isClient = user?.role === 'client';

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAdmin, isClient }}>
      {children}
    </AuthContext.Provider>
  );
};
